import { T, useGT } from 'gt-react';
import {
  CheckCircleIcon,
  CircleIcon,
  ProhibitIcon,
  PaperclipIcon,
} from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import type { LabelingMailboxItem, SD849DispositionValue } from '@erp/rpc';
import {
  classifierVerdictDisplay,
  type ClassifiedBand,
  type ClassifiedDecision,
  type ClassifierVerdictDisplay,
} from './classifierVerdictDisplay';

interface Props {
  items: LabelingMailboxItem[];
  isLoading: boolean;
  onOpen: (inboundEmailId: string) => void;
}

/**
 * The browse list — every mailbox email with its current label state.
 *
 * Answers the questions the review queue can't: find a specific email, see how much
 * is left, check what was decided before. The status column is the point, which is
 * why labeled rows stay visible rather than disappearing.
 */
export function LabelingBrowseTable({ items, isLoading, onOpen }: Props) {
  const gt = useGT();

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <Card className="p-10 text-center text-muted-foreground">
        <T context="labeling browse empty state">
          No emails match this filter.
        </T>
      </Card>
    );
  }

  return (
    <Card className="divide-y">
      {items.map((item) => (
        <button
          key={item.inboundEmailId}
          type="button"
          onClick={() => onOpen(item.inboundEmailId)}
          className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-muted/50"
        >
          <StatusCell
            disposition={item.label?.disposition ?? null}
            isComplete={item.label?.isComplete ?? false}
            gt={gt}
          />

          <span className="w-52 shrink-0 truncate font-mono text-xs text-muted-foreground">
            {item.from}
          </span>

          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate text-sm">
              {item.subject ??
                gt('(no subject)', {
                  context: 'labeling email missing subject',
                })}
            </span>
            <VerdictLine
              verdict={classifierVerdictDisplay(item.classification)}
              gt={gt}
            />
          </span>

          {item.attachmentCount > 0 ? (
            <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
              <PaperclipIcon className="h-3 w-3" />
              {item.attachmentCount}
            </span>
          ) : null}

          <span className="w-40 shrink-0 truncate text-xs">
            {item.detectedSupplier.supplierName ?? (
              <span className="text-muted-foreground">—</span>
            )}
          </span>

          <Badge variant="outline" className="shrink-0 font-normal">
            {Math.round(item.confidence.isAn849 * 100)}%
          </Badge>
        </button>
      ))}
    </Card>
  );
}

/** Unlabeled / labeled-as-X, the column that makes browse worth having. */
function StatusCell({
  disposition,
  isComplete,
  gt,
}: {
  disposition: SD849DispositionValue | null;
  isComplete: boolean;
  gt: (s: string, o?: { context?: string }) => string;
}) {
  const ctx = { context: 'labeling browse status' };
  // An is_849 whose supplier is missing or older than the disposition is NOT done —
  // it still sits in the Unlabeled filter, so showing it as a finished "849" would
  // have the column contradict the filter it was reached through.
  if (disposition === 'is_849' && !isComplete) {
    return (
      <span className="flex w-28 shrink-0 items-center gap-1.5 text-xs text-amber-600 dark:text-amber-500">
        <CircleIcon className="h-4 w-4" />
        {gt('Needs supplier', ctx)}
      </span>
    );
  }
  if (disposition === 'is_849') {
    return (
      <span className="flex w-28 shrink-0 items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-500">
        <CheckCircleIcon className="h-4 w-4" weight="fill" />
        {gt('849', ctx)}
      </span>
    );
  }
  if (disposition === 'not_849') {
    return (
      <span className="flex w-28 shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
        <ProhibitIcon className="h-4 w-4" />
        {gt('Not an 849', ctx)}
      </span>
    );
  }
  if (disposition === 'context_for_849') {
    return (
      <span className="flex w-28 shrink-0 items-center gap-1.5 text-xs text-sky-600 dark:text-sky-500">
        <CheckCircleIcon className="h-4 w-4" />
        {gt('Context', ctx)}
      </span>
    );
  }
  return (
    <span className="flex w-28 shrink-0 items-center gap-1.5 text-xs text-amber-600 dark:text-amber-500">
      <CircleIcon className="h-4 w-4" />
      {gt('Unlabeled', ctx)}
    </span>
  );
}

/**
 * What the classifier decided, and why.
 *
 * Prefixed "Classifier", because the row ALREADY carries a human verdict in
 * `StatusCell` and the two vocabularies overlap almost word for word — "Not an 849"
 * means one thing when a person said it and another when the machine did. Two
 * unlabelled facts in one row would reintroduce at the surface exactly the
 * conflation this line was added to end.
 *
 * The REASON gets the elastic space and everything else is `shrink-0`, so the thing
 * a reviewer acts on is the last thing to be clipped rather than the first. The
 * probability is deliberately not rendered at all: reviewers here are domain experts
 * who act on reasons, and a second percentage beside the rule-based triage score
 * would read as a competing claim about the same email.
 *
 * "Never classified" is a first-class state, not an empty cell. Everything below the
 * skip floor is by design never put in front of anyone, and this listing is the
 * agreed safety net for that population: it only works if an email nothing ever read
 * looks different from one we read and rejected.
 */
function VerdictLine({
  verdict,
  gt,
}: {
  verdict: ClassifierVerdictDisplay;
  gt: (s: string, o?: { context?: string }) => string;
}) {
  const ctx = { context: 'labeling browse classifier verdict' };
  const prefix = (
    <span className="shrink-0 text-muted-foreground/70">
      {gt('Classifier', ctx)}
    </span>
  );

  if (verdict.kind === 'unclassified') {
    return (
      <span className="flex min-w-0 items-baseline gap-1.5 text-xs">
        {prefix}
        <span className="truncate text-muted-foreground/70 italic">
          {gt('never ran', ctx)}
        </span>
      </span>
    );
  }

  if (verdict.kind === 'skipped') {
    return (
      <span className="flex min-w-0 items-baseline gap-1.5 text-xs">
        {prefix}
        <ReasonText reason={verdict.reason} gt={gt} />
        <span className="shrink-0 font-medium text-muted-foreground">
          {gt('skipped', ctx)}
        </span>
      </span>
    );
  }

  const decisionLabel: Record<ClassifiedDecision, string> = {
    is_849: gt('is an 849', ctx),
    not_849: gt('not an 849', ctx),
    context_for_849: gt('context for an 849', ctx),
  };
  // The band names, unabbreviated. A row with no probability makes NO band claim at
  // all — its stored band was computed on a different quantity, and an unknown
  // rendered as `ignore` would be a confident negative nobody ever measured.
  const bandLabel: Record<ClassifiedBand, string> = {
    act: gt('act', ctx),
    needs_decision: gt('needs a decision', ctx),
    ignore: gt('ignore', ctx),
  };

  return (
    <span className="flex min-w-0 items-baseline gap-1.5 text-xs">
      {prefix}
      <ReasonText reason={verdict.reason} gt={gt} />
      <span className="shrink-0 font-medium text-foreground/70">
        {decisionLabel[verdict.decision]}
        {verdict.band ? ` · ${bandLabel[verdict.band]}` : null}
      </span>
    </span>
  );
}

/**
 * The classifier's reason, on one line but readable in full.
 *
 * It has to truncate — this is a dense list and a reason can run to a sentence —
 * but a truncated primary signal that cannot be read at all is not visibility. The
 * native `title` tooltip carries the whole thing at any column width, with no
 * expand state to manage in a row that is itself a button.
 */
function ReasonText({
  reason,
  gt,
}: {
  reason: string | null;
  gt: (s: string, o?: { context?: string }) => string;
}) {
  const text =
    reason ??
    gt('no reason recorded', { context: 'labeling browse classifier verdict' });
  return (
    <span
      title={text}
      className="min-w-0 flex-1 truncate text-muted-foreground"
    >
      {text}
    </span>
  );
}
